import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";

export interface LiveStream {
  id: string;
  streamer_id: string | null;
  platform: string;
  title: string;
  url: string;
  thumbnail?: string | null;
  viewer_count: number;
  is_live: boolean;
  started_at?: string | null;
  ended_at?: string | null;
  tags?: string[] | null;
  coin_mentions?: string[] | null;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Streamer {
  id: string;
  user_wallet: string;
  platform: string;
  username: string;
  display_name: string;
  profile_image?: string | null;
  follower_count: number;
  is_verified: boolean;
  is_active: boolean;
  vote_multiplier: number;
  total_streams: number;
  total_views: number;
  last_stream_at?: string | null;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export function useLiveStreams(limit = 20) {
  const queryClient = useQueryClient();
  const [isSubscribed, setIsSubscribed] = useState(false);

  const { data: streams = [], isLoading, error } = useQuery({
    queryKey: ["live-streams", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("live_streams")
        .select("*, streamers(*)")
        .eq("is_live", true)
        .order("viewer_count", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as (LiveStream & { streamers: Streamer | null })[];
    },
  });

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel("live-streams-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "live_streams",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["live-streams"] });
        }
      )
      .subscribe((status) => {
        setIsSubscribed(status === "SUBSCRIBED");
      });

    return () => {
      supabase.removeChannel(channel);
      setIsSubscribed(false);
    };
  }, [queryClient]);

  return {
    streams,
    isLoading,
    error,
    isSubscribed,
    liveCount: streams.length,
    totalViewers: streams.reduce((sum, s) => sum + s.viewer_count, 0),
  };
}

export function useStreamers(limit = 50) {
  return useQuery({
    queryKey: ["streamers", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("streamers")
        .select("*")
        .eq("is_active", true)
        .order("follower_count", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as Streamer[];
    },
  });
}

export function useStreamerProfile(walletAddress?: string) {
  const { publicKey } = useWallet();
  const wallet = walletAddress || publicKey;

  return useQuery({
    queryKey: ["streamer", wallet],
    queryFn: async () => {
      if (!wallet) return null;

      const { data, error } = await supabase
        .from("streamers")
        .select("*")
        .eq("user_wallet", wallet)
        .maybeSingle();

      if (error) throw error;
      return data as Streamer | null;
    },
    enabled: !!wallet,
  });
}

export function useStreamManagement() {
  const { publicKey } = useWallet();
  const queryClient = useQueryClient();

  // Get streamer's streams
  const { data: streamerProfile } = useStreamerProfile();

  const { data: myStreams = [], isLoading: isLoadingMyStreams } = useQuery({
    queryKey: ["my-streams", streamerProfile?.id],
    queryFn: async () => {
      if (!streamerProfile) return [];

      const { data, error } = await supabase
        .from("live_streams")
        .select("*")
        .eq("streamer_id", streamerProfile.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as LiveStream[];
    },
    enabled: !!streamerProfile,
  });

  // Start stream mutation
  const startStreamMutation = useMutation({
    mutationFn: async (data: {
      platform: string;
      title: string;
      url: string;
      thumbnail?: string;
      tags?: string[];
      coinMentions?: string[];
    }) => {
      if (!streamerProfile) throw new Error("You must be a registered streamer");

      const { error } = await supabase.from("live_streams").insert({
        streamer_id: streamerProfile.id,
        platform: data.platform,
        title: data.title,
        url: data.url,
        thumbnail: data.thumbnail,
        tags: data.tags,
        coin_mentions: data.coinMentions,
        is_live: true,
        started_at: new Date().toISOString(),
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-streams", streamerProfile?.id] });
      queryClient.invalidateQueries({ queryKey: ["live-streams"] });
      toast({
        title: "Stream Started",
        description: "Your stream is now live!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Start Stream",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // End stream mutation
  const endStreamMutation = useMutation({
    mutationFn: async (streamId: string) => {
      const { error } = await supabase
        .from("live_streams")
        .update({
          is_live: false,
          ended_at: new Date().toISOString(),
        })
        .eq("id", streamId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-streams", streamerProfile?.id] });
      queryClient.invalidateQueries({ queryKey: ["live-streams"] });
      toast({
        title: "Stream Ended",
      });
    },
  });

  // Update viewer count (for external tracking)
  const updateViewerCountMutation = useMutation({
    mutationFn: async ({ streamId, viewerCount }: { streamId: string; viewerCount: number }) => {
      const { error } = await supabase
        .from("live_streams")
        .update({ viewer_count: viewerCount })
        .eq("id", streamId);

      if (error) throw error;
    },
  });

  return {
    streamerProfile,
    myStreams,
    activeStream: myStreams.find((s) => s.is_live),
    startStream: startStreamMutation.mutateAsync,
    endStream: endStreamMutation.mutateAsync,
    updateViewerCount: updateViewerCountMutation.mutateAsync,
    isStarting: startStreamMutation.isPending,
    isEnding: endStreamMutation.isPending,
    isLoadingMyStreams,
  };
}

// Streams by coin - shows streams mentioning a specific coin
export function useStreamsByCoin(coinId: string) {
  return useQuery({
    queryKey: ["streams-by-coin", coinId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("live_streams")
        .select("*, streamers(*)")
        .contains("coin_mentions", [coinId])
        .eq("is_live", true)
        .order("viewer_count", { ascending: false });

      if (error) throw error;
      return data as (LiveStream & { streamers: Streamer | null })[];
    },
    enabled: !!coinId,
  });
}

// Top streamers by various metrics
export function useTopStreamers(
  metric: "followers" | "views" | "streams" = "followers",
  limit = 10
) {
  const { data, error, refetch } = useQuery({
    queryKey: ["top-streamers", metric, limit],
    queryFn: async () => {
      let query = supabase
        .from("streamers")
        .select("*")
        .eq("is_active", true)
        .limit(limit);

      switch (metric) {
        case "followers":
          query = query.order("follower_count", { ascending: false });
          break;
        case "views":
          query = query.order("total_views", { ascending: false });
          break;
        case "streams":
          query = query.order("total_streams", { ascending: false });
          break;
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Streamer[];
    },
  });

  return { data, error, refetch };
}
